
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageSquare, Send, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formSchema } from "@/types/social-groups";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AddGroupDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  isRTL: boolean;
}

export const AddGroupDialog: React.FC<AddGroupDialogProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  isRTL
}) => {
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name_ar: "",
      name_en: "",
      description_ar: "",
      description_en: "",
      type: "whatsapp",
      link: "",
      members: 0,
      image_url: null,
    },
  });

  const fetchTelegramInfo = async () => {
    const link = form.getValues("link");
    
    if (!link) {
      toast.error(isRTL ? "يرجى إدخال الرابط أولاً" : "Please enter a link first");
      return;
    }

    if (!link.includes("t.me") && !link.includes("telegram.me")) {
      toast.error(isRTL ? "هذه الخاصية متاحة فقط لقنوات تليجرام العامة" : "This feature is only available for public Telegram channels");
      return;
    }

    setIsFetching(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('fetch-telegram-info', {
        body: { link }
      });

      if (error) {
        console.error('Error fetching telegram info:', error);
        toast.error(isRTL ? "خطأ في جلب البيانات" : "Error fetching data");
        return;
      }

      if (!data.success) {
        toast.error(data.error || (isRTL ? "فشل في جلب البيانات" : "Failed to fetch data"));
        return;
      }

      const info = data.data;
      
      // Update form with fetched data
      form.setValue("name_ar", info.title);
      form.setValue("name_en", info.title);
      form.setValue("description_ar", info.description || "");
      form.setValue("description_en", info.description || "");
      form.setValue("type", "telegram");
      form.setValue("members", info.members || 0);
      
      // Set image if available
      if (info.image_url) {
        form.setValue("image_url", info.image_url);
        setPreviewImage(info.image_url);
      }
      
      toast.success(isRTL ? "تم جلب البيانات بنجاح!" : "Data fetched successfully!");
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(isRTL ? "خطأ في الاتصال" : "Connection error");
    } finally {
      setIsFetching(false);
    }
  };

  const handleFormSubmit = (data: z.infer<typeof formSchema>) => {
    onSubmit(data);
    form.reset();
    setPreviewImage(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        form.reset();
        setPreviewImage(null);
      }
      setIsOpen(open);
    }}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {isRTL ? "إضافة مجموعة جديدة" : "Add New Group"}
          </DialogTitle>
          <DialogDescription>
            {isRTL
              ? "أدخل تفاصيل مجموعة التواصل الاجتماعي أدناه."
              : "Enter social media group details below."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            {/* Link field first with fetch button */}
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isRTL ? "رابط المجموعة" : "Group Link"}</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input 
                        placeholder={isRTL ? "أدخل رابط المجموعة" : "Enter group link"} 
                        {...field} 
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchTelegramInfo}
                      disabled={isFetching}
                      className="shrink-0"
                    >
                      {isFetching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-1" />
                          {isRTL ? "جلب تلقائي" : "Auto Fetch"}
                        </>
                      )}
                    </Button>
                  </div>
                  <FormDescription>
                    {isRTL
                      ? "لقنوات تليجرام العامة، اضغط 'جلب تلقائي' لملء البيانات"
                      : "For public Telegram channels, click 'Auto Fetch' to fill data"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Image preview */}
            {previewImage && (
              <div className="flex justify-center">
                <img 
                  src={previewImage} 
                  alt="Channel photo" 
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "نوع المجموعة" : "Group Type"}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={isRTL ? "اختر نوع المجموعة" : "Select group type"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="whatsapp">
                          <div className="flex items-center gap-2">
                            <MessageSquare size={16} className="text-green-500" />
                            <span>{isRTL ? "واتساب" : "WhatsApp"}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="telegram">
                          <div className="flex items-center gap-2">
                            <Send size={16} className="text-blue-500" />
                            <span>{isRTL ? "تلجرام" : "Telegram"}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="members"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "عدد الأعضاء" : "Members Count"}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الاسم (عربي)" : "Name (Arabic)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "اسم المجموعة بالعربية" : "Group name in Arabic"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الاسم (إنجليزي)" : "Name (English)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "اسم المجموعة بالإنجليزية" : "Group name in English"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الوصف (عربي)" : "Description (Arabic)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "وصف المجموعة بالعربية" : "Group description in Arabic"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isRTL ? "الوصف (إنجليزي)" : "Description (English)"}</FormLabel>
                    <FormControl>
                      <Input placeholder={isRTL ? "وصف المجموعة بالإنجليزية" : "Group description in English"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <DialogFooter>
              <Button type="submit">{isRTL ? "إضافة المجموعة" : "Add Group"}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
